import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CheckIcon, ChevronDownIcon, Layers, LoaderIcon } from 'lucide-react';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import {
  ModelScopeFactory,
  useStatefullyAwareInScopeModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { SubAgentChatModel } from './sub-agent-chat.model';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { cn } from '@cotera/client/v0/lib/utils';
import type {
  CustomToolRenderer,
  ToolRendererProps,
} from '@cotera/client/v0/chat/tool-renderers';
import { formatCredits } from '@cotera/client/v0/components/topbar/credit-balance-section';
import { FadeText } from './sub-agent-tool-renderer';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';
import {
  SubAgentChatPanel,
  SubAgentScrollContext,
} from './sub-agent-chat-renderer';
import { useToolName } from '@cotera/client/v0/chat/tool-renderers/ask-for-credentials-renderer';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import {
  extractForEachData,
  forEachToolPartRevisionKey,
  type ForEachItem,
} from './for-each-tool-extract';
import { previewValue } from './for-each-item-label';

// ─── ForEachSubAgentItem ────────────────────────────────────────────────────

export const ForEachSubAgentItem: React.FC<{
  conversationId: string;
  label: string;
  namingToolKey: string;
  triggeringToolIncomplete: boolean;
  readonly?: boolean;
}> = ({
  conversationId,
  label,
  namingToolKey,
  triggeringToolIncomplete,
  readonly = true,
}) => {
  // While for-each is streaming, `chatIds` can grow a render ahead of
  // `ModelScopeFactory` registering the matching models (registration happens in
  // an effect that runs after commit). During that gap the model for a freshly
  // added chatId is not yet in scope; render a placeholder instead of letting
  // `SubAgentChatPanel`'s non-null assertion fire. Subscribing to the scope here
  // re-renders us as soon as the model lands.
  const model = useStatefullyAwareInScopeModel(
    SubAgentChatModel,
    conversationId
  );

  return (
    <li className="list-none p-2">
      {model === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
          <LoaderIcon className="h-4 w-4 shrink-0 animate-spin" />
          <span className="italic text-sm">Starting {label}…</span>
        </div>
      ) : (
        <SubAgentChatPanel
          modelScopeKey={conversationId}
          toolKeyForDisplay={namingToolKey}
          displayNameOverride={label}
          triggeringToolIncomplete={triggeringToolIncomplete}
          readonly={readonly}
          maxHeight="400px"
          marginBottom={false}
        />
      )}
    </li>
  );
};

// ─── Aggregate status helper ────────────────────────────────────────────────

/**
 * Reads a single sub-agent's status & credits via hooks.
 * Renders nothing — just reports values up via the onStats callback.
 */
const SubAgentStatsReporterInner: React.FC<{
  model: SubAgentChatModel;
  conversationId: string;
  onStats: (conversationId: string, running: boolean, credits: number) => void;
}> = ({ model, conversationId, onStats }) => {
  const status = useWatchableValue(model.statusWatchable);
  const credits = useWatchableValue(model.creditsWatchable);

  const isRunning = status === 'streaming' || status === 'submitted';

  useEffect(() => {
    onStats(conversationId, isRunning, credits);
  }, [conversationId, isRunning, credits, onStats]);

  return null;
};

export const SubAgentStatsReporter: React.FC<{
  conversationId: string;
  onStats: (conversationId: string, running: boolean, credits: number) => void;
}> = ({ conversationId, onStats }) => {
  // While for-each is streaming, `chatIds` can grow a render ahead of
  // `ModelScopeFactory` registering the matching models. During that gap the
  // model for a freshly added chatId is not yet in scope — tolerate the
  // transient null rather than dereferencing it. Subscribing to the scope here
  // re-renders us (and reports stats) as soon as the model lands.
  const model = useStatefullyAwareInScopeModel(
    SubAgentChatModel,
    conversationId
  );

  if (model === null) {
    return null;
  }

  return (
    <SubAgentStatsReporterInner
      model={model}
      conversationId={conversationId}
      onStats={onStats}
    />
  );
};

// ─── ForEachTool ────────────────────────────────────────────────────────────

const ForEachTool: React.FC<{
  chatIds: string[];
  items: ForEachItem[];
  namingToolKey: string;
  triggeringToolIncomplete: boolean;
  readonly?: boolean;
}> = ({
  chatIds,
  items,
  namingToolKey,
  triggeringToolIncomplete,
  readonly,
}) => {
  const total = chatIds.length;

  // Aggregate stats from per-sub-agent reporters
  const statsRef = useRef<Map<string, { running: boolean; credits: number }>>(
    new Map()
  );
  const [aggregate, setAggregate] = useState({
    completedCount: 0,
    totalCredits: 0,
  });

  const handleStats = useCallback(
    (conversationId: string, running: boolean, credits: number) => {
      statsRef.current.set(conversationId, { running, credits });
      let completed = 0;
      let totalCr = 0;
      for (const s of statsRef.current.values()) {
        if (!s.running) {
          completed++;
        }
        totalCr += s.credits;
      }
      setAggregate({ completedCount: completed, totalCredits: totalCr });
    },
    []
  );

  const { completedCount, totalCredits } = aggregate;
  const allDone = completedCount === total;
  const anyRunning = completedCount < total;

  const [isOpen, setIsOpen] = useState(true);

  return (
    <>
      {/* Invisible reporters that feed aggregate stats */}
      {chatIds.map((id) => (
        <SubAgentStatsReporter
          key={`stats-${id}`}
          conversationId={id}
          onStats={handleStats}
        />
      ))}
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="group not-prose w-full"
      >
        <div className="flex items-center gap-2 py-1">
          <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-2 text-left min-w-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
              <Layers className="h-4 w-4 shrink-0" />
              {anyRunning ? (
                <LoaderIcon className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />
              )}
              {anyRunning ? (
                <RunningToolTitle className="italic text-sm min-w-0">
                  <FadeText
                    text={
                      allDone
                        ? `For Each — ${total} complete`
                        : `For Each — ${completedCount}/${total} complete`
                    }
                  />
                </RunningToolTitle>
              ) : (
                <FadeText
                  text={
                    allDone
                      ? `For Each — ${total} complete`
                      : `For Each — ${completedCount}/${total} complete`
                  }
                  className="italic"
                />
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {totalCredits > 0 && (
                <span className="text-xs text-muted-foreground/70 font-normal not-italic whitespace-nowrap shrink-0">
                  {anyRunning ? '~' : ''}
                  {formatCredits(totalCredits)} credits
                </span>
              )}
              <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </div>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent
          className={cn(
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:slide-in-from-top-2'
          )}
        >
          <div className="mt-2 rounded-md border border-border bg-muted/30 overflow-hidden">
            <div className="px-3 py-2 text-xs text-muted-foreground/60 font-mono border-b border-border">
              {namingToolKey}
            </div>
            {/*
              Single scroll region for the whole list: items render at full
              height (SubAgentScrollContext=true disables their individual
              400px scrollers) so a long list of ~20+ items scrolls here as one
              instead of overflowing the panel unreachably. onWheel is contained
              so the wheel isn't lost to ancestor scroll.
            */}
            <div
              className="max-h-[70vh] overflow-y-auto overscroll-y-contain"
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              <SubAgentScrollContext.Provider value={true}>
                <ul className="divide-y divide-border">
                  {chatIds.map((chatId, i) => {
                    const item = items[i];
                    const explicitLabel =
                      item?.label !== undefined && item.label !== ''
                        ? item.label
                        : null;
                    const derivedLabel =
                      item?.input !== undefined
                        ? previewValue(item.input)
                        : null;
                    const itemLabel =
                      explicitLabel ?? derivedLabel ?? `Item ${i + 1}`;
                    return (
                      <ForEachSubAgentItem
                        key={chatId}
                        conversationId={chatId}
                        label={itemLabel}
                        namingToolKey={namingToolKey}
                        triggeringToolIncomplete={triggeringToolIncomplete}
                        readonly={readonly}
                      />
                    );
                  })}
                </ul>
              </SubAgentScrollContext.Provider>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
};

// ─── ForEachChatRenderer (outer wrapper) ────────────────────────────────────

const ForEachChatRenderer = React.memo<{
  part: ToolRendererProps['part'];
  readonly?: boolean;
}>(
  ({ part, readonly }) => {
    const client = useTenantedClient();
    const watchablesFactory = useWatchablesFactory();
    const eventConfig = useMemo(
      () => ({ client: client.resource.chat }),
      [client.resource.chat]
    );

    const toolName = useToolName(part.toolKey().toString());
    const { chatIds, items } = useMemo(() => extractForEachData(part), [part]);

    // Stable identity for the set of child sub-agent conversations. Each model
    // is keyed by its `chatId`, so the set only needs to be (re)created when the
    // chatIds themselves change — never on an unrelated tool-part data update.
    const chatIdsKey = chatIds.join('|');

    const createModels = useCallback(
      () =>
        chatIds.map(
          (chatId) =>
            new SubAgentChatModel({
              part,
              eventConfig,
              conversationIdOverride: chatId,
              modelId: chatId,
              watchablesFactory,
            })
        ),
      [part, eventConfig, chatIds, watchablesFactory]
    );

    if (chatIds.length === 0) {
      return (
        <div className="flex items-center gap-2 text-sm italic text-muted-foreground py-1">
          <LoaderIcon className="h-4 w-4 animate-spin" />
          <span>Starting For Each…</span>
        </div>
      );
    }

    return (
      // Key the scope on the *set of chatIds* (plus the stable client/factory),
      // NOT on `part`. Keying on `part` rebuilt every SubAgentChatModel on each
      // streamed for-each update; every fresh model fires a `runOnMount`
      // sub-agent-snapshot fetch and the subtree unmounts/remounts, cascading
      // synchronous writes into the shared HijackerQueueModel. With many items
      // that storm of XHRs + re-renders tripped "Maximum update depth exceeded".
      <ModelScopeFactory
        createModels={createModels}
        deps={[chatIdsKey, eventConfig, watchablesFactory]}
      >
        <ForEachTool
          chatIds={chatIds}
          items={items}
          namingToolKey={toolName ?? 'coco/sub-agent'}
          triggeringToolIncomplete={part.isIncomplete()}
          readonly={readonly}
        />
      </ModelScopeFactory>
    );
  },
  (prev, next) => {
    // Include raw output/state — sub-agent snapshots often update the tool **result**
    // row without changing serialized `input`, and extractForEachData reads `subAgentChatIds`
    // from `output` in that case.
    return (
      prev.part.toolCallId === next.part.toolCallId &&
      forEachToolPartRevisionKey(prev.part) ===
        forEachToolPartRevisionKey(next.part) &&
      prev.readonly === next.readonly
    );
  }
);

/**
 * Create a ForEachToolRenderer.
 * Used by v1-tool-renderers to handle coco/for-each tool calls.
 */
export function createForEachToolRenderer(): CustomToolRenderer {
  return (props) => (
    <ForEachChatRenderer part={props.part} readonly={props.readonly} />
  );
}
